import {
  clarificationsOf,
  critiquesOf,
  latestBuildSpec,
  personasOf,
  scanResultOf,
  type Idea,
} from "../data/types";

/**
 * Renders the whole "living doc" for an idea as a single Markdown document —
 * raw capture, clarifications, scan, personas + critique scores, and the
 * latest build spec. Used by the IdeaDetail export action so the user can
 * paste / archive the full session, not just the spec.
 */
export function ideaToMarkdown(idea: Idea): string {
  const out: string[] = [];
  const date = new Date(idea.updatedAtMs).toISOString().slice(0, 10);

  out.push(`# ${idea.title}`);
  out.push("");
  out.push(`_State: ${idea.state} · Last updated: ${date}_`);
  out.push("");

  out.push("## Raw capture");
  out.push(idea.rawCapture.trim().length > 0 ? idea.rawCapture.trim() : "_(no additional notes)_");
  out.push("");

  const clar = clarificationsOf(idea);
  if (clar.length > 0) {
    out.push("## Clarification (Mom-Test)");
    clar.forEach((c, i) => {
      out.push(`### Q${i + 1}: ${c.question}`);
      out.push(c.answer);
      out.push("");
    });
  }

  const scan = scanResultOf(idea);
  if (scan && scan.products.length > 0) {
    out.push("## Market scan");
    scan.products.forEach((p) => {
      out.push(`- **${p.name}** — ${p.tagline}`);
      if (p.rationale) out.push(`  - _${p.rationale}_`);
    });
    out.push("");
  }

  const personas = personasOf(idea);
  const critiques = critiquesOf(idea);
  if (personas.length > 0) {
    out.push("## Personas + critique");
    personas.forEach((p) => {
      out.push(`### ${p.name} — ${p.descriptor}`);
      if (p.evidenceSource) out.push(`_Evidence: ${p.evidenceSource}_`);
      out.push(`_Voice: ${p.voiceSignature}_`);
      out.push(`- Jobs-to-be-done: ${p.jobsToBeDone}`);
      out.push(`- Current alternative: ${p.currentAlternative}`);
      if (p.mustHaves.length > 0) out.push(`- Must-haves: ${p.mustHaves.join("; ")}`);
      if (p.dealbreakers.length > 0) out.push(`- Dealbreakers: ${p.dealbreakers.join("; ")}`);

      const c = critiques.find((cr) => cr.personaId === p.id);
      if (c) {
        out.push("");
        out.push(`**Critique** — problem-fit ${c.problemFitScore}/5 · switch ${c.switchLikelihoodScore}/5`);
        if (c.reactionSummary) out.push(`> ${c.reactionSummary.replace(/\n/g, "\n> ")}`);
        if (c.questionsBack.length > 0) {
          out.push("");
          out.push("Questions back:");
          c.questionsBack.forEach((q) => out.push(`- ${q}`));
        }
        if (c.mustHaves.length > 0) out.push(`Must-haves: ${c.mustHaves.join("; ")}`);
        if (c.dealbreakers.length > 0) out.push(`Dealbreakers: ${c.dealbreakers.join("; ")}`);
      }
      out.push("");
    });
  }

  const spec = latestBuildSpec(idea);
  if (spec) {
    out.push("## Build spec");
    out.push(
      `_Generated ${new Date(spec.generatedAtMs).toISOString()} via ${spec.source}_`,
    );
    out.push("");
    out.push(spec.markdown);
  }

  return out.join("\n");
}

export function downloadIdeaAsMarkdown(idea: Idea): void {
  const md = ideaToMarkdown(idea);
  const blob = new Blob([md], { type: "text/markdown;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const safeName = idea.title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  a.download = `${safeName || "idea"}.md`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function copyIdeaAsMarkdown(idea: Idea): Promise<boolean> {
  const md = ideaToMarkdown(idea);
  try {
    await navigator.clipboard.writeText(md);
    return true;
  } catch {
    window.prompt("Copy idea Markdown:", md);
    return false;
  }
}
